"use client";

// useRing — the private ring's data, for the two surfaces that show it: the
// per-app controls (AccessControl: a connection's settings, an api key's row) and the requests and
// history on the you page (PrivateRing). One fetch shape, one set of writes.
//
// Every write answers at once (law_snappy): the view moves first, the request
// follows, and a write that did not land puts the view back and says so.

import { useCallback, useEffect, useRef, useState } from "react";
import type { Depth, GrantLength, WriteMode } from "../../lib/self/ring";
import type { Category } from "../../lib/self/categories";
import type { RingApp, RingPayload } from "../../lib/self/ringView";
import { RING_CHANGED_EVENT, ringChanged } from "../../lib/self/ringEvents";

export type RingState =
  | { status: "loading" }
  /** not for this person (the feature gate), or signed out: render nothing */
  | { status: "hidden" }
  | { status: "error" }
  | { status: "ready"; data: RingPayload };

export type Decision = "allowed_once" | "allowed_always" | "denied";

let seq = 0;

export function useRing() {
  const [state, setState] = useState<RingState>({ status: "loading" });
  const [note, setNote] = useState<string | null>(null);
  const alive = useRef(true);
  // this hook's own name on the shared event, so it never reloads on its own echo
  const [me] = useState(() => `ring-${++seq}`);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/you/ring", { cache: "no-store" });
      if (!alive.current) return;
      if (res.status === 404 || res.status === 401) return setState({ status: "hidden" });
      if (!res.ok) return setState((s) => (s.status === "ready" ? s : { status: "error" }));
      const data = (await res.json()) as RingPayload;
      if (alive.current) setState({ status: "ready", data });
    } catch {
      if (alive.current) setState((s) => (s.status === "ready" ? s : { status: "error" }));
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    // the read on mount: its setState lands after the fetch, never in this pass
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  // a decision or a setting made on another surface (the inbox, the access
  // sheet, the other panel): read again, so every view says the same thing
  useEffect(() => {
    const on = (e: Event) => {
      if ((e as CustomEvent<{ from?: string }>).detail?.from !== me) void load();
    };
    window.addEventListener(RING_CHANGED_EVENT, on);
    return () => window.removeEventListener(RING_CHANGED_EVENT, on);
  }, [load, me]);

  /** Answer one request, for how long and for what (149). The row leaves at once; the ring reloads after. */
  const decide = useCallback(
    async (requestId: string, decision: Decision, how?: { length?: GrantLength; categories?: Category[] | null }) => {
      const was: { data: RingPayload | null } = { data: null };
      setState((s) => {
        if (s.status !== "ready") return s;
        was.data = s.data;
        return { status: "ready", data: { ...s.data, requests: s.data.requests.filter((r) => r.id !== requestId) } };
      });
      setNote(null);
      try {
        const res = await fetch("/api/you/ring", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            request_id: requestId,
            decision,
            ...(how?.length ? { length: how.length } : {}),
            ...(how?.categories ? { categories: how.categories } : {}),
          }),
        });
        if (!res.ok && res.status !== 409) throw new Error("decide");
        ringChanged(me);
        await load();
      } catch {
        const back = was.data;
        if (back) setState({ status: "ready", data: back });
        setNote("that did not go through. please try again.");
      }
    },
    [load, me],
  );

  /** Set one app's depth and whether it asks first. */
  const setApp = useCallback(
    async (app: Pick<RingApp, "kind" | "id">, depth: Depth, askFirst: boolean) => {
      const was: { data: RingPayload | null } = { data: null };
      setState((s) => {
        if (s.status !== "ready") return s;
        was.data = s.data;
        return {
          status: "ready",
          data: {
            ...s.data,
            apps: s.data.apps.map((a) => (a.kind === app.kind && a.id === app.id ? { ...a, depth, askFirst } : a)),
            // a save that asks first (or goes shallower) ends that app's open windows
            windows: askFirst ? s.data.windows.filter((w) => !(w.app.kind === app.kind && w.app.id === app.id)) : s.data.windows,
          },
        };
      });
      setNote(null);
      try {
        const res = await fetch("/api/you/ring", {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ kind: app.kind, id: app.id, depth, ask_first: askFirst }),
        });
        if (!res.ok) throw new Error("set");
        ringChanged(me);
      } catch {
        const back = was.data;
        if (back) setState({ status: "ready", data: back });
        setNote("that did not save. please try again.");
      }
    },
    [me],
  );

  /** One call to the ring that changes something: the view has already moved; a miss puts it back. */
  const send = useCallback(
    async (method: "POST" | "PATCH", body: Record<string, unknown>, was: { data: RingPayload | null } | null, fail: string) => {
      setNote(null);
      try {
        const res = await fetch("/api/you/ring", { method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
        if (!res.ok && res.status !== 409) throw new Error("ring");
        ringChanged(me);
        await load();
        return true;
      } catch {
        // read only now: the move's updater has run by the time the request came back
        const back = was?.data;
        if (back) setState({ status: "ready", data: back });
        setNote(fail);
        return false;
      }
    },
    [load, me],
  );

  /** Move the view now; the holder says what it was, once the update has run. */
  const move = useCallback((next: (d: RingPayload) => RingPayload): { data: RingPayload | null } => {
    const was: { data: RingPayload | null } = { data: null };
    setState((s) => {
      if (s.status !== "ready") return s;
      was.data = s.data;
      return { status: "ready", data: next(s.data) };
    });
    return was;
  }, []);

  /** Let an app read more, for a while or always, maybe only some of you (149). */
  const grant = useCallback(
    (app: Pick<RingApp, "kind" | "id">, depth: Exclude<Depth, "standard">, length: GrantLength, categories: Category[] | null) =>
      send("POST", { action: "grant", kind: app.kind, id: app.id, depth, length, categories }, null, "that did not save. please try again."),
    [send],
  );

  /** End one grant now. It leaves the view at once. */
  const endGrant = useCallback(
    (requestId: string) => {
      const was = move((d) => ({ ...d, windows: d.windows.filter((w) => w.requestId !== requestId) }));
      return send("POST", { action: "end", request_id: requestId }, was, "that did not go through. please try again.");
    },
    [move, send],
  );

  /** Set what one app may write (149). */
  const setWrite = useCallback(
    (app: Pick<RingApp, "kind" | "id">, mode: WriteMode) => {
      const was = move((d) => ({ ...d, apps: d.apps.map((a) => (a.kind === app.kind && a.id === app.id ? { ...a, writeMode: mode } : a)) }));
      return send("PATCH", { kind: app.kind, id: app.id, write_mode: mode }, was, "that did not save. please try again.");
    },
    [move, send],
  );

  /** Keep or let go of a write an app left for a yes. It says what is happening at once. */
  const answerHeld = useCallback(
    (proposalId: string, decision: "keep" | "drop") => {
      const was = move((d) => ({
        ...d,
        proposals: d.proposals.map((p) =>
          p.id === proposalId ? { ...p, status: decision === "keep" ? "kept" : "dropped", outcome: decision === "keep" ? "keeping it…" : "letting it go…", preview: null } : p,
        ),
      }));
      return send("POST", { action: decision, proposal_id: proposalId }, was, "that did not go through. please try again.");
    },
    [move, send],
  );

  return { state, note, reload: load, decide, setApp, grant, endGrant, setWrite, answerHeld };
}
